/**
 * ProjectOverviewView — `/projects/$projectId/overview`
 *
 * Tab default del proyecto. Alineada al prototipo (Pantalla 6):
 *  - Charter del proyecto (placeholder hasta que el artifact `tpl-charter`
 *    exista en B.5.e+ con catálogo seed real)
 *  - Próximo gate (computado del lifecycle — la primera fase con status
 *    `in_progress` o la siguiente `not_started`)
 *  - Equipo principal (consume listProjectMembers limit 5)
 *  - Métricas (oficiales, drafts, CRs, avance global)
 *
 * Para el avance global: una heurística simple = approved_phases /
 * active_phases. El backend no expone aún un % oficial.
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ShieldCheck } from 'lucide-react'

import { ApiError } from '../../api/client'
import {
  fetchProjectLifecycle,
  type ProjectPhase,
  type ProjectPhaseStatus,
} from '../../api/projects'
import {
  listProjectMembers,
  type ProjectMember,
} from '../../api/project_members'
import { useAccessToken } from '../../auth/useAccessToken'
import { roleLabel, type RoleOption } from '../../components/projects/team/roles'
import { ScrollPage } from '../../components/shell/ScrollPage'
import { useProjectRoles } from '../../hooks/useProjectRoles'
import { useProjectContext } from './ProjectShell'

export function ProjectOverviewView() {
  const { project } = useProjectContext()
  const { accessToken } = useAccessToken()

  const lifecycleQuery = useQuery({
    queryKey: ['projects', project.id, 'lifecycle'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return fetchProjectLifecycle(accessToken, project.id)
    },
    enabled: !!accessToken,
    staleTime: 30_000,
    retry: false,
  })

  const membersQuery = useQuery({
    queryKey: ['projects', project.id, 'members', 'all'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return listProjectMembers(accessToken, project.id)
    },
    enabled: !!accessToken,
    staleTime: 30_000,
    retry: false,
  })
  const rolesQuery = useProjectRoles(project.id)

  return (
    <ScrollPage padding="tight">
      <div className="max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <CharterCard />
          <NextGateCard
            phases={lifecycleQuery.data?.phases}
            loading={lifecycleQuery.isLoading}
            error={lifecycleQuery.error}
          />
          <CoreTeamCard
            members={membersQuery.data ?? []}
            roles={rolesQuery.data ?? []}
            loading={membersQuery.isLoading}
            error={membersQuery.error}
          />
          <MetricsCard
            phases={lifecycleQuery.data?.phases}
            loading={lifecycleQuery.isLoading}
            error={lifecycleQuery.error}
          />
        </div>
      </div>
    </ScrollPage>
  )
}

// ─── Charter (placeholder hasta seed real B.6) ─────────────────────────────
function CharterCard() {
  const { t } = useTranslation('projects')
  return (
    <article className="lg:col-span-2 bg-surface border border-line rounded-md p-6">
      <h2 className="font-display text-lg text-ink mb-3">
        {t('overview.charter_title')}
      </h2>
      <p className="font-body text-sm text-ink-muted">
        {t('overview.charter_empty')}
      </p>
      <p className="mt-2 font-mono text-xs text-ink-subtle italic">
        {t('overview.charter_pending_hint')}
      </p>
    </article>
  )
}

// ─── Next gate (computed) ──────────────────────────────────────────────────
function NextGateCard({
  phases,
  loading,
  error,
}: {
  phases: ProjectPhase[] | undefined
  loading: boolean
  error: unknown
}) {
  const { t } = useTranslation('projects')

  const nextPhase = useMemo(() => {
    if (!phases) return null
    return findNextGatePhase(phases)
  }, [phases])

  return (
    <article className="bg-surface border border-line rounded-md p-6">
      <p className="font-ui text-xs uppercase tracking-wide text-ink-subtle">
        {t('overview.next_gate_title')}
      </p>
      {loading ? (
        <p className="mt-2 font-mono text-xs text-ink-subtle">…</p>
      ) : error ? (
        <ErrorBox error={error} />
      ) : !nextPhase ? (
        <p className="mt-2 font-body text-sm text-ink-muted">
          {t('overview.next_gate_empty')}
        </p>
      ) : (
        <NextGateDetail phase={nextPhase} />
      )}
    </article>
  )
}

function NextGateDetail({ phase }: { phase: ProjectPhase }) {
  const phaseName = phase.name || phase.phase_code
  const { t } = useTranslation('projects')
  const total =
    (phase.artifact_counts.draft ?? 0) +
    (phase.artifact_counts.in_review ?? 0) +
    (phase.artifact_counts.approved ?? 0)
  const approved = phase.artifact_counts.approved ?? 0
  return (
    <>
      <h3 className="mt-2 font-display text-base text-ink">{phaseName}</h3>
      {phase.gate_description ? (
        <p className="mt-1 font-body text-sm text-ink-muted">
          {phase.gate_description}
        </p>
      ) : null}
      <p className="mt-3 font-mono text-xs text-ink-subtle">
        {approved}/{total} ·{' '}
        {t(`phase_status.${phase.status}` as const)}
      </p>
    </>
  )
}

/** Heurística: la siguiente phase con status='in_progress' o
 * 'not_started'. Skipped no cuenta. */
function findNextGatePhase(phases: ProjectPhase[]): ProjectPhase | null {
  const ordered = [...phases].sort((a, b) => a.phase_order - b.phase_order)
  // Primero in_progress
  const inProgress = ordered.find((p) => p.status === 'in_progress')
  if (inProgress) return inProgress
  // Si no hay in_progress, la siguiente not_started no skipped
  const next = ordered.find(
    (p) =>
      p.status === ('not_started' as ProjectPhaseStatus) &&
      p.contract_mode !== 'skipped',
  )
  return next ?? null
}

// ─── Core team ─────────────────────────────────────────────────────────────
function CoreTeamCard({
  members,
  roles,
  loading,
  error,
}: {
  members: ProjectMember[]
  roles: RoleOption[]
  loading: boolean
  error: unknown
}) {
  const { t } = useTranslation('projects')
  const top5 = members.slice(0, 5)

  return (
    <article className="bg-surface border border-line rounded-md p-6">
      <h2 className="font-display text-lg text-ink mb-3">
        {t('overview.team_title')}
      </h2>
      {loading ? (
        <p className="font-mono text-xs text-ink-subtle">…</p>
      ) : error ? (
        <ErrorBox error={error} />
      ) : top5.length === 0 ? (
        <p className="font-body text-sm text-ink-muted">
          {t('overview.team_empty')}
        </p>
      ) : (
        <ul className="space-y-3 text-sm">
          {top5.map((m) => (
            <MemberRow key={m.id} member={m} roles={roles} />
          ))}
        </ul>
      )}
    </article>
  )
}

function MemberRow({ member, roles }: { member: ProjectMember; roles: RoleOption[] }) {
  // Prioridad: display_name (resuelto del JOIN) > user_email > external_email > '—'.
  // Nunca el UUID — confunde al usuario.
  const primary =
    member.user_display_name ??
    member.user_email ??
    member.external_email ??
    '—'
  // Línea secundaria: si hay nombre y email distintos, mostramos el email.
  const secondary =
    member.user_display_name &&
    (member.user_email ?? member.external_email)
      ? (member.user_email ?? member.external_email)
      : null
  const initials = buildInitials(
    member.user_display_name ??
      member.user_email ??
      member.external_email ??
      '?',
  )
  return (
    <li className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-full bg-brand-soft text-brand grid place-items-center text-xs font-semibold shrink-0">
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-ink truncate">{primary}</p>
        <p className="text-xs text-ink-muted truncate">
          {secondary ? (
            <>
              {secondary}
              <span className="text-ink-subtle"> · </span>
            </>
          ) : null}
          {member.roles.map((key) => roleLabel(key, roles)).join(', ')}
          {member.raci_role ? ` · ${member.raci_role}` : ''}
        </p>
      </div>
    </li>
  )
}

function buildInitials(value: string): string {
  const trimmed = value.trim()
  if (!trimmed) return '··'
  const at = trimmed.indexOf('@')
  const head = at > 0 ? trimmed.slice(0, at) : trimmed
  const parts = head.split(/[\s.\-_]+/).filter(Boolean)
  if (parts.length === 0) return '··'
  if (parts.length === 1) {
    const word = parts[0] ?? ''
    return word.slice(0, 2).toUpperCase()
  }
  const first = parts[0]?.[0] ?? ''
  const second = parts[1]?.[0] ?? ''
  return (first + second).toUpperCase()
}

// ─── Métricas ──────────────────────────────────────────────────────────────
function MetricsCard({
  phases,
  loading,
  error,
}: {
  phases: ProjectPhase[] | undefined
  loading: boolean
  error: unknown
}) {
  const { t } = useTranslation('projects')

  const stats = useMemo(() => {
    if (!phases)
      return {
        official: '—' as string | number,
        drafts: '—' as string | number,
        crs: '—' as string | number,
        progress: '—' as string,
      }
    let official = 0
    let drafts = 0
    let inReview = 0
    let approvedPhases = 0
    let activePhases = 0
    for (const p of phases) {
      if (p.contract_mode === 'skipped') continue
      activePhases += 1
      if (p.status === 'approved') approvedPhases += 1
      // API field key is 'canon' — surface as official in the UI.
      official += p.artifact_counts.canon ?? 0
      drafts += p.artifact_counts.draft ?? 0
      inReview += p.artifact_counts.in_review ?? 0
    }
    const pct =
      activePhases > 0
        ? Math.round((approvedPhases / activePhases) * 100)
        : 0
    return {
      official,
      drafts: drafts + inReview,
      crs: 0, // Fase E
      progress: `${pct}%`,
    }
  }, [phases])

  return (
    <article className="lg:col-span-2 bg-surface border border-line rounded-md p-6">
      <h2 className="font-display text-lg text-ink mb-4">
        {t('overview.metrics_title')}
      </h2>
      {error ? (
        <ErrorBox error={error} />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricCell
            label={t('overview.metrics.official_published')}
            value={loading ? '…' : stats.official}
          />
          <MetricCell
            label={t('overview.metrics.drafts_in_review')}
            value={loading ? '…' : stats.drafts}
            accent="review"
          />
          <MetricCell
            label={t('overview.metrics.open_crs')}
            value={loading ? '…' : stats.crs}
            accent="high"
          />
          <MetricCell
            label={t('overview.metrics.progress')}
            value={loading ? '…' : stats.progress}
          />
        </div>
      )}
      <div className="mt-5 pt-5 border-t border-line flex items-center justify-between text-xs">
        <span className="text-ink-muted">{t('overview.hash_chain_label')}</span>
        <span className="flex items-center gap-1.5 text-official">
          <ShieldCheck className="w-3.5 h-3.5" />
          {t('overview.hash_chain_intact')}
        </span>
      </div>
    </article>
  )
}

function MetricCell({
  label,
  value,
  accent,
}: {
  label: string
  value: string | number
  accent?: 'review' | 'high' | undefined
}) {
  const valueClass =
    accent === 'review'
      ? 'text-review'
      : accent === 'high'
        ? 'text-high'
        : 'text-ink'
  return (
    <div>
      <p className="text-xs text-ink-muted">{label}</p>
      <p className={`mt-1 font-display text-2xl font-semibold ${valueClass}`}>
        {value}
      </p>
    </div>
  )
}

// ─── ErrorBox (reusable inline) ────────────────────────────────────────────
function ErrorBox({ error }: { error: unknown }) {
  const { t } = useTranslation(['projects', 'errors'])
  const code = error instanceof ApiError ? error.code : 'UNKNOWN'
  const fallback =
    error instanceof ApiError
      ? error.body?.message ?? error.message
      : error instanceof Error
        ? error.message
        : 'unknown'
  return (
    <div className="rounded-md border border-critical/40 bg-critical-soft p-3 font-mono text-xs text-critical">
      {t(`errors:${code}` as const, { defaultValue: fallback })}
    </div>
  )
}
